import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Visibility of the Positions list's optional columns, persisted per user on the server.
 * The server value arrives with the first list response (`meta.columns`); until then, and
 * for anyone with no stored preference, localStorage then the defaults answer.
 *
 * A stored map is only used when it names every column with a boolean; anything else
 * falls through to the next source, so a malformed preference reopens on the defaults.
 */
const STORAGE_KEY = 'positions-list-columns-v1';

const PREFERENCE_MODULE = 'positions_list_columns';

export const COLUMN_UNIQUE_ID = 'unique_id';
export const COLUMN_EMPLOYEE_NUMBER = 'employee_number';
export const COLUMN_BUDGET_CODE = 'budget_code';
export const COLUMN_VENUE_PERCENT = 'venue_percent';
export const COLUMN_END_DATE = 'end_date';

const COLUMN_KEYS = [COLUMN_UNIQUE_ID, COLUMN_EMPLOYEE_NUMBER, COLUMN_BUDGET_CODE, COLUMN_VENUE_PERCENT, COLUMN_END_DATE];

const DEFAULTS = {
    [COLUMN_UNIQUE_ID]: false,
    [COLUMN_EMPLOYEE_NUMBER]: false,
    [COLUMN_BUDGET_CODE]: false,
    [COLUMN_VENUE_PERCENT]: true,
    [COLUMN_END_DATE]: true,
};

const validate = (candidate) => {
    if (!candidate || typeof candidate !== 'object') return null;

    const healed = {};
    const complete = COLUMN_KEYS.every((key) => {
        if (typeof candidate[key] !== 'boolean') {
            return false;
        }
        healed[key] = candidate[key];
        return true;
    });

    return complete ? healed : null;
};

const loadFromStorage = () => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;

        return validate(JSON.parse(raw));
    } catch (e) {
        return null;
    }
};

const saveToStorage = (value) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch (e) {
        // Ignore quota / privacy-mode failures — visibility still works for the session.
    }
};

/** Fire-and-forget: a failed save only costs the choice on the next page load. */
async function persistColumns(value) {
    try {
        const response = await fetch('/preferences', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content ?? '',
                Accept: 'application/json',
            },
            body: JSON.stringify({ module: PREFERENCE_MODULE, value }),
        });

        if (!response.ok) {
            console.warn('[usePositionsColumns] Column visibility was not saved', response.status);
        }
    } catch (e) {
        console.warn('[usePositionsColumns] Failed to persist column visibility', e);
    }
}

/**
 * @param serverColumns `meta.columns` from the list response: undefined until it has loaded,
 *                      null when the user has no stored preference.
 */
export default function usePositionsColumns(serverColumns) {
    const [columns, setColumns] = useState(() => loadFromStorage() ?? DEFAULTS);
    const serverAdopted = useRef(false);
    const toggledBeforeLoad = useRef(false);

    useEffect(() => {
        if (serverAdopted.current || serverColumns === undefined) return;
        serverAdopted.current = true;

        // A toggle made before the list loaded is the newer choice, and it is already saved.
        if (toggledBeforeLoad.current) return;

        const fromServer = validate(serverColumns);
        if (fromServer) {
            setColumns(fromServer);
            saveToStorage(fromServer);
            return;
        }

        // No usable server value: adopt what this browser remembers, so it follows the user.
        const stored = loadFromStorage();
        if (stored) {
            persistColumns(stored);
        }
    }, [serverColumns]);

    const toggle = useCallback((key) => {
        toggledBeforeLoad.current = !serverAdopted.current;

        setColumns((prev) => {
            const next = { ...prev, [key]: !prev[key] };
            saveToStorage(next);
            persistColumns(next);

            return next;
        });
    }, []);

    const definitions = useMemo(
        () => [
            { key: COLUMN_END_DATE, label: $t('End date') },
            { key: COLUMN_VENUE_PERCENT, label: $t('Venue %') },
            { key: COLUMN_UNIQUE_ID, label: $t('Unique Id') },
            { key: COLUMN_EMPLOYEE_NUMBER, label: $t('Employee number') },
            { key: COLUMN_BUDGET_CODE, label: $t('Budget code') },
        ],
        [],
    );

    return { columns, toggle, definitions };
}
